import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Calendar,
  Car,
  Clock,
  Receipt,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  X,
  Plus,
  Edit,
  Trash2,
  Save,
  Check,
  ExternalLink,
} from "lucide-react";
import axios from "axios";
import { API_URL } from "../utils/api-config";
import Button from "../components/ui/Button";
import BackButton from "../components/ui/BackButton";
import { Card, CardContent } from "../components/ui/Card";
import Chip from "../components/ui/Chip";
import Spinner from "../components/ui/Spinner";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "../components/ui/Dialog";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import Switch from "../components/ui/Switch";
import IconButton from "../components/ui/IconButton";
import Alert from "../components/ui/Alert";
import DamagesSection from "../components/DamagesSection";

// Interfaces
interface Fine {
  _id: string;
  amount: number;
  reason: string;
  location: string;
  referenceNumber: string;
  date?: string;
  isPaid: boolean;
  images?: string[];
}

const REASON_LABELS: Record<string, string> = {
  amende_simple: "Amende simple",
  exces_vitesse: "Excès de vitesse",
};

interface FineFormData {
  date: string;
  amount: number;
  reason: string;
  location: string;
  referenceNumber: string;
  isPaid: boolean;
  images?: string[];
}

interface Report {
  _id: string;
  type: string;
  description: string;
  location: string;
  referenceNumber: string;
  date?: string;
  isResolved: boolean;
  isResponsible: boolean;
  dateAccident?: string;
  dateDeclaration?: string;
  typeChocInitial?: string;
  dommageCorporel?: boolean;
  constatTransmis?: boolean;
  responsabiliteChauffeur?: string;
  images?: string[];
  documents?: string[];
}

const TYPE_CHOC_OPTIONS = [
  { value: "collision_frontale", label: "Collision frontale" },
  { value: "collision_arriere", label: "Collision par l'arrière" },
  { value: "collision_laterale", label: "Collision latérale/oblique" },
  { value: "carambolage", label: "Carambolage" },
  { value: "collision_obstacle_fixe", label: "Collision avec obstacle fixe" },
  { value: "collision_pieton", label: "Collision avec un piéton" },
  { value: "collision_trottinette", label: "Collision avec une trottinette" },
  { value: "collision_cycliste", label: "Collision avec un cycliste" },
  { value: "collision_moto", label: "Collision avec une moto" },
  { value: "collision_animal", label: "Collision avec un animal" },
  { value: "autre", label: "Autre type de collision" },
];

const RESPONSABILITE_OPTIONS = [
  { value: "non_responsable", label: "Chauffeur non responsable" },
  { value: "responsable_50", label: "Chauffeur responsable à 50%" },
  { value: "responsable_100", label: "Chauffeur responsable à 100%" },
];

interface ReportFormData {
  type: string;
  description: string;
  location: string;
  referenceNumber: string;
  isResolved: boolean;
  isResponsible: boolean;
  dateAccident: string;
  dateDeclaration: string;
  typeChocInitial: string;
  dommageCorporel: boolean;
  constatTransmis: boolean;
  responsabiliteChauffeur: string;
  images?: string[];
  documents?: string[];
}

interface DamageStats {
  rayures?: number;
  chocs?: number;
  manquant?: number;
  casse?: number;
}

interface DriverAssignment {
  _id: string;
  vehiclePlate: string;
  date: string;
  time?: string;
  status: string;
  endDate?: string;
  endTime?: string;
  purpose?: string;
  destination?: string;
  currentStep?: string;
  damages?: DamageStats;
  fines?: Fine[];
  reports?: Report[];
}

interface DriverInfo {
  firstName: string;
  lastName: string;
  driverId: string;
  licenseType?: string;
  licenseNumber?: string;
  phone1?: string;
}

interface DriverHistory {
  history: DriverAssignment[];
  driverInfo: DriverInfo;
  totalDamages?: DamageStats;
  fines?: Fine[];
  reports?: Report[];
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR");
};

const DriverHistoryPage = () => {
  const { id } = useParams<{ id: string }>();
  const [history, setHistory] = useState<DriverHistory | null>(null);
  const [driverKey, setDriverKey] = useState(id || "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fines, setFines] = useState<Fine[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [loadingFines, setLoadingFines] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [expandedFine, setExpandedFine] = useState<string | null>(null);
  const [expandedReport, setExpandedReport] = useState<string | null>(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [fineFormOpen, setFineFormOpen] = useState(false);
  const [reportFormOpen, setReportFormOpen] = useState(false);
  const [currentFine, setCurrentFine] = useState<Fine | null>(null);
  const [currentReport, setCurrentReport] = useState<Report | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<"fine" | "report" | null>(null);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [uploadingFineImage, setUploadingFineImage] = useState(false);
  const [uploadingReportImage, setUploadingReportImage] = useState(false);
  const [previewFineImages, setPreviewFineImages] = useState<string[]>([]);
  const [previewReportImages, setPreviewReportImages] = useState<string[]>([]);
  const [reportTab, setReportTab] = useState<'all' | 'non_responsable' | 'responsable_50' | 'responsable_100'>('all');
  const [fineTab, setFineTab] = useState<'all' | 'amende_simple' | 'exces_vitesse'>('all');
  const [periodStart, setPeriodStart] = useState<string>("");
  const [periodEnd, setPeriodEnd] = useState<string>("");
  const [periodOpen, setPeriodOpen] = useState(false);
  
  // États pour l'analyse automatique de constats
  const [analysisResult, setAnalysisResult] = useState<{
    reference?: string;
    date?: string;
    location?: string;
    summary?: string;
  } | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadedConstatName, setUploadedConstatName] = useState<string | null>(null);
  
  // États pour l'analyse automatique d'amendes
  const [fineAnalysisResult, setFineAnalysisResult] = useState<{
    amount?: string;
    reason?: string;
    location?: string;
    reference?: string;
    summary?: string;
  } | null>(null);
  const [fineAnalysisError, setFineAnalysisError] = useState<string | null>(null);
  const [analyzingFine, setAnalyzingFine] = useState(false);
  const [uploadedFineName, setUploadedFineName] = useState<string | null>(null);
  
  // États pour l'analyse automatique de sinistres
  const [reportAnalysisResult, setReportAnalysisResult] = useState<{
    type?: string;
    description?: string;
    location?: string;
    reference?: string;
    summary?: string;
  } | null>(null);
  const [reportAnalysisError, setReportAnalysisError] = useState<string | null>(null);
  const [analyzingReport, setAnalyzingReport] = useState(false);
  const [uploadedReportName, setUploadedReportName] = useState<string | null>(null);

  const [fineFormData, setFineFormData] = useState<FineFormData>({
    date: new Date().toISOString().split("T")[0],
    amount: 0,
    reason: "",
    location: "",
    referenceNumber: "",
    isPaid: false,
    images: [],
  });

  const [reportFormData, setReportFormData] = useState<ReportFormData>({
    type: "",
    description: "",
    location: "",
    referenceNumber: "",
    isResolved: false,
    isResponsible: false,
    dateAccident: "",
    dateDeclaration: "",
    typeChocInitial: "",
    dommageCorporel: false,
    constatTransmis: false,
    responsabiliteChauffeur: "",
    images: [],
    documents: [],
  });

  // Calcul du total des amendes
  const totalFinesAmount = fines.reduce((sum, fine) => sum + (Number(fine.amount) || 0), 0);

  const isWithinPeriod = (raw?: string) => {
    if (!periodStart && !periodEnd) return true;
    if (!raw) return false;
    const t = new Date(raw).getTime();
    if (Number.isNaN(t)) return false;
    if (periodStart && t < new Date(periodStart).getTime()) return false;
    if (periodEnd && t > new Date(periodEnd).getTime() + 24 * 60 * 60 * 1000 - 1) return false;
    return true;
  };

  // Filtrage des amendes
  const filteredFines = fines.filter(fine => {
    if (!isWithinPeriod(fine.date)) return false;
    if (fineTab === "all") return true;
    return fine.reason === fineTab;
  });

  // Filtrage des constats par responsabilité
  const filteredReports = reports.filter(report => {
    if (!isWithinPeriod(report.dateAccident || report.date)) return false;
    if (reportTab === "all") return true;
    return report.responsabiliteChauffeur === reportTab;
  });

  const handleConstatFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAnalyzing(true);
    setAnalysisError(null);
    setUploadedConstatName(file.name);

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const content = typeof reader.result === "string" ? reader.result : "";
        const reference =
          content.match(/(CST[-\s]?\d{4}[-\s]?\d+)/i)?.[1] || file.name;
        const date = content.match(/(\d{2}\/\d{2}\/\d{4})/)?.[1];
        const location =
          content.match(/(?:lieu|location|adresse)[:\s]*([^\n\r]+)/i)?.[1]?.trim();
        setAnalysisResult({
          reference: reference || "Non détectée",
          date: date || "Non détectée",
          location: location || "Non détectée",
          summary: content.slice(0, 400) || "Aperçu non disponible",
        });
      } catch (e) {
        setAnalysisError("Lecture impossible du fichier.");
      } finally {
        setAnalyzing(false);
      }
    };
    reader.onerror = () => {
      setAnalysisError("Erreur de lecture du fichier.");
      setAnalyzing(false);
    };
    reader.readAsText(file);
  };

  const handleFineFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAnalyzingFine(true);
    setFineAnalysisError(null);
    setUploadedFineName(file.name);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/ai/analyze-fine`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setFineAnalysisResult(response.data.data);
      } else {
        setFineAnalysisError("Analyse échouée");
      }
    } catch (error: any) {
      console.error("Erreur analyse amende IA:", error);
      setFineAnalysisError(
        error.response?.data?.message || "Erreur lors de l'analyse de l'amende"
      );
    } finally {
      setAnalyzingFine(false);
    }
  };

  const handleReportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAnalyzingReport(true);
    setReportAnalysisError(null);
    setUploadedReportName(file.name);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/ai/analyze-report`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setReportAnalysisResult(response.data.data);
      } else {
        setReportAnalysisError("Analyse échouée");
      }
    } catch (error: any) {
      console.error("Erreur analyse constat IA:", error);
      setReportAnalysisError(
        error.response?.data?.message || "Erreur lors de l'analyse du constat"
      );
    } finally {
      setAnalyzingReport(false);
    }
  };

  const fetchDriverHistory = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/drivers/${id}/history`);
      console.log("=== DONNÉES HISTORIQUE CHAUFFEUR ===", data);
      console.log("Driver Info:", data.driverInfo);
      console.log("History:", data.history);
      console.log("Total Damages:", data.totalDamages);
      setHistory(data);
      if (data?.driverInfo?.driverId) {
        setDriverKey(data.driverInfo.driverId);
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || "Erreur lors du chargement de l'historique");
      console.error("Erreur:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFines = async (targetId: string) => {
        setLoadingFines(true);
    try {
      const { data } = await axios.get(`${API_URL}/drivers/${targetId}/fines`);
      // Le backend renvoie { driverId, firstName, lastName, fines }
      setFines(Array.isArray(data.fines) ? data.fines : Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des amendes:", error);
        setFines([]);
      } finally {
        setLoadingFines(false);
      }
    };

    const fetchReports = async (targetId: string) => {
        setLoadingReports(true);
    try {
      const { data } = await axios.get(`${API_URL}/drivers/${targetId}/reports`);
      // Le backend renvoie { driverId, firstName, lastName, reports }
      setReports(Array.isArray(data.reports) ? data.reports : Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des constats:", error);
        setReports([]);
      } finally {
        setLoadingReports(false);
      }
    };

  useEffect(() => {
    if (id) {
      fetchDriverHistory();
    }
  }, [id]);

  useEffect(() => {
    if (driverKey) {
      fetchFines(driverKey);
      fetchReports(driverKey);
    }
  }, [driverKey]);

  const driverInfo = {
    firstName: history?.driverInfo?.firstName || "",
    lastName: history?.driverInfo?.lastName || "",
    driverId: driverKey || "",
    phone1: history?.driverInfo?.phone1 || "",
    licenseType: history?.driverInfo?.licenseType || "",
    licenseNumber: history?.driverInfo?.licenseNumber || "",
  };

  // Fonctions pour les amendes
  const handleOpenFineForm = (fine?: Fine) => {
    if (fine) {
      setCurrentFine(fine);
      setFineFormData({
        date: fine.date ? new Date(fine.date).toISOString().split("T")[0] : new Date().toISOString().split("T")[0],
        amount: fine.amount,
        reason: fine.reason,
        location: fine.location,
        referenceNumber: fine.referenceNumber,
        isPaid: fine.isPaid,
        images: fine.images || [],
      });
      setPreviewFineImages(fine.images || []);
      setIsEditing(true);
    } else {
      setCurrentFine(null);
      setFineFormData({
        date: new Date().toISOString().split("T")[0],
        amount: 0,
        reason: "",
        location: "",
        referenceNumber: "",
        isPaid: false,
        images: [],
      });
      setPreviewFineImages([]);
      setIsEditing(false);
    }
    setFineFormOpen(true);
  };

  const handleCloseFineForm = () => {
    setFineFormOpen(false);
    setCurrentFine(null);
    setIsEditing(false);
  };

  const handleFineFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const target = e.target as HTMLInputElement;
    setFineFormData({
      ...fineFormData,
      [name]: target.type === "checkbox" ? target.checked : value,
    });
  };

  const handleFineImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploadingFineImage(true);

    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setPreviewFineImages([...previewFineImages, ...newPreviews]);

    setTimeout(() => {
      setFineFormData({
        ...fineFormData,
        images: [...(fineFormData.images || []), ...newPreviews],
      });
      setUploadingFineImage(false);
    }, 1000);
  };

  const removeFinePreviewImage = (index: number) => {
    const newPreviews = [...previewFineImages];
    newPreviews.splice(index, 1);
    setPreviewFineImages(newPreviews);

    const newImages = [...(fineFormData.images || [])];
    newImages.splice(index, 1);
    setFineFormData({ ...fineFormData, images: newImages });
  };

  const handleFineSubmit = async () => {
    try {
      if (isEditing && currentFine) {
        await axios.put(
          `${API_URL}/drivers/${id}/fines/${currentFine._id}`,
          fineFormData
        );
        setSnackbarMessage("Amende modifiée avec succès");
      } else {
        await axios.post(`${API_URL}/drivers/${id}/fines`, fineFormData);
        setSnackbarMessage("Amende ajoutée avec succès");
      }
      setSnackbarOpen(true);
      handleCloseFineForm();
      fetchFines(id || "");
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de l'enregistrement de l'amende");
    }
  };

  const handleDeleteFine = async (fineId: string) => {
    try {
      await axios.delete(`${API_URL}/drivers/${id}/fines/${fineId}`);
      setSnackbarMessage("Amende supprimée avec succès");
    setSnackbarOpen(true);
      fetchFines(id || "");
      setDeleteConfirmationOpen(false);
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la suppression");
    }
  };

  // Fonctions pour les constats
  const handleOpenReportForm = (report?: Report) => {
    if (report) {
      setCurrentReport(report);
      setReportFormData({
        type: report.type,
        description: report.description,
        location: report.location,
        referenceNumber: report.referenceNumber,
        isResolved: report.isResolved,
        isResponsible: report.isResponsible,
        dateAccident: report.dateAccident ? new Date(report.dateAccident).toISOString().split("T")[0] : "",
        dateDeclaration: report.dateDeclaration ? new Date(report.dateDeclaration).toISOString().split("T")[0] : "",
        typeChocInitial: report.typeChocInitial || "",
        dommageCorporel: report.dommageCorporel || false,
        constatTransmis: report.constatTransmis || false,
        responsabiliteChauffeur: report.responsabiliteChauffeur || "",
        images: report.images || [],
        documents: report.documents || [],
      });
      setPreviewReportImages(report.images || []);
      setIsEditing(true);
    } else {
      setCurrentReport(null);
      setReportFormData({
        type: "",
        description: "",
        location: "",
        referenceNumber: "",
        isResolved: false,
        isResponsible: false,
        dateAccident: "",
        dateDeclaration: "",
        typeChocInitial: "",
        dommageCorporel: false,
        constatTransmis: false,
        responsabiliteChauffeur: "",
        images: [],
        documents: [],
      });
      setPreviewReportImages([]);
      setIsEditing(false);
    }
    setReportFormOpen(true);
  };

  const handleCloseReportForm = () => {
    setReportFormOpen(false);
    setCurrentReport(null);
    setIsEditing(false);
  };

  const handleReportFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const target = e.target as HTMLInputElement;
    setReportFormData({
      ...reportFormData,
      [name]: target.type === "checkbox" ? target.checked : value,
    });
  };

  const handleReportImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploadingReportImage(true);

    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setPreviewReportImages([...previewReportImages, ...newPreviews]);

    setTimeout(() => {
      setReportFormData({
        ...reportFormData,
        images: [...(reportFormData.images || []), ...newPreviews],
      });
      setUploadingReportImage(false);
    }, 1000);
  };

  const removeReportPreviewImage = (index: number) => {
    const newPreviews = [...previewReportImages];
    newPreviews.splice(index, 1);
    setPreviewReportImages(newPreviews);

    const newImages = [...(reportFormData.images || [])];
    newImages.splice(index, 1);
    setReportFormData({ ...reportFormData, images: newImages });
  };

  const handleReportSubmit = async () => {
    try {
      if (isEditing && currentReport) {
        await axios.put(
          `${API_URL}/drivers/${id}/reports/${currentReport._id}`,
          reportFormData
        );
        setSnackbarMessage("Constat modifié avec succès");
      } else {
        await axios.post(`${API_URL}/drivers/${id}/reports`, reportFormData);
        setSnackbarMessage("Constat ajouté avec succès");
      }
      setSnackbarOpen(true);
      handleCloseReportForm();
      fetchReports(id || "");
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de l'enregistrement du constat");
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    try {
      await axios.delete(`${API_URL}/drivers/${id}/reports/${reportId}`);
      setSnackbarMessage("Constat supprimé avec succès");
      setSnackbarOpen(true);
      fetchReports(id || "");
      setDeleteConfirmationOpen(false);
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la suppression");
    }
  };

  const handleOpenImage = (img: string) => {
    setCurrentImage(img);
    setImageDialogOpen(true);
  };

  const openDeleteConfirmation = (type: "fine" | "report", itemId: string) => {
    setDeleteType(type);
    setItemToDelete(itemId);
    setDeleteConfirmationOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteType === "fine" && itemToDelete) {
      handleDeleteFine(itemToDelete);
    } else if (deleteType === "report" && itemToDelete) {
      handleDeleteReport(itemToDelete);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-blue-50 to-gray-50 py-4 md:py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Container principal */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
          {/* En-tête */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex items-center gap-4">
              <BackButton
                fallbackPath="/chauffeurs"
                label="Retour aux chauffeurs"
              />
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                Historique du chauffeur
                <button
                  type="button"
                  onClick={() => setPeriodOpen(o => !o)}
                  className={`p-1 rounded transition-colors ${periodStart || periodEnd ? "text-blue-600 bg-blue-50" : "text-gray-500 hover:bg-gray-100"}`}
                  title="Filtrer par période"
                  aria-label="Filtrer par période"
                >
                  <Calendar className="w-6 h-6" />
                </button>
              </h1>
            </div>
          </div>

          {periodOpen && (
            <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Du</label>
                <input
                  type="date"
                  value={periodStart}
                  max={periodEnd || undefined}
                  onChange={e => setPeriodStart(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Au</label>
                <input
                  type="date"
                  value={periodEnd}
                  min={periodStart || undefined}
                  onChange={e => setPeriodEnd(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
              {(periodStart || periodEnd) && (
                <button
                  type="button"
                  onClick={() => { setPeriodStart(""); setPeriodEnd(""); }}
                  className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Réinitialiser
                </button>
              )}
              <span className="text-xs text-gray-500 ml-auto">
                Filtre les amendes et constats par date
              </span>
            </div>
          )}

          {/* Carte d'informations du chauffeur */}
          <Card className="mb-6">
            <CardContent>
              <h2 className="text-lg font-semibold text-primary mb-4">
                Informations du chauffeur
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">ID Chauffeur</p>
                  <p className="font-medium text-gray-900">{driverInfo.driverId || id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Nom complet</p>
                  <p className="font-medium text-gray-900">
                    {driverInfo.firstName} {driverInfo.lastName}
                  </p>
                </div>
                {driverInfo.phone1 && (
                  <div>
                    <p className="text-sm text-gray-600">Téléphone</p>
                    <p className="font-medium text-gray-900">{driverInfo.phone1}</p>
                  </div>
                )}
                {driverInfo.licenseType && (
                  <div>
                    <p className="text-sm text-gray-600">Type de permis</p>
                    <p className="font-medium text-gray-900">{driverInfo.licenseType}</p>
                  </div>
                )}
                {driverInfo.licenseNumber && (
                  <div>
                    <p className="text-sm text-gray-600">Numéro de permis</p>
                    <p className="font-medium text-gray-900">{driverInfo.licenseNumber}</p>
                  </div>
                )}
              </div>
              
              {/* Statistiques totales des dégâts */}
              {history?.totalDamages && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Statistiques des dégâts totaux :</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="flex items-center gap-2">
                      <Chip size="small" label={history.totalDamages.rayures || 0} color={history.totalDamages.rayures > 0 ? "warning" : "default"} />
                      <span className="text-sm text-gray-600">Rayures</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Chip size="small" label={history.totalDamages.chocs || 0} color={history.totalDamages.chocs > 0 ? "error" : "default"} />
                      <span className="text-sm text-gray-600">Chocs</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Chip size="small" label={history.totalDamages.manquant || 0} color={history.totalDamages.manquant > 0 ? "warning" : "default"} />
                      <span className="text-sm text-gray-600">Manquants</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Chip size="small" label={history.totalDamages.casse || 0} color={history.totalDamages.casse > 0 ? "error" : "default"} />
                      <span className="text-sm text-gray-600">Cassés</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section Amendes */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Receipt className="w-6 h-6 text-primary" />
                  Amendes ({fines.length})
                </h2>
                {fines.length > 0 && (
                  <Chip 
                    label={`Total: ${totalFinesAmount.toFixed(2)}€`}
                    color="warning"
                    size="medium"
                  />
                )}
              </div>
            <Button
              variant="contained"
              size="small"
                startIcon={<Plus className="w-4 h-4" />}
              onClick={() => handleOpenFineForm()}
            >
              Ajouter une amende
            </Button>
            </div>

            {/* Analyse automatique d'amende */}
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                Analyse automatique d'une amende
              </h3>
              <p className="text-xs text-blue-700 mb-3">
                Uploadez un fichier (PDF, image, texte) d'amende pour extraire automatiquement les informations.
              </p>
              <input
                type="file"
                accept=".pdf,.txt,.jpg,.jpeg,.png"
                onChange={handleFineFile}
                className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
              />
              {analyzingFine && (
                <div className="mt-3 flex items-center gap-2">
                  <Spinner size="small" />
                  <span className="text-sm text-gray-600">Analyse en cours...</span>
                </div>
              )}
              {fineAnalysisError && (
                <Alert severity="error" className="mt-3">
                  {fineAnalysisError}
                </Alert>
              )}
              {fineAnalysisResult && (
                <div className="mt-3 p-3 bg-white border border-green-300 rounded">
                  <h4 className="text-sm font-semibold text-green-800 mb-2">✅ Données extraites :</h4>
                  <div className="space-y-1 text-sm">
                    <p><strong>Référence :</strong> {fineAnalysisResult.reference}</p>
                    <p><strong>Montant :</strong> {fineAnalysisResult.amount} €</p>
                    <p><strong>Motif :</strong> {fineAnalysisResult.reason}</p>
                    <p><strong>Lieu :</strong> {fineAnalysisResult.location}</p>
                    <details className="mt-2">
                      <summary className="cursor-pointer text-blue-600 hover:text-blue-800">Voir l'aperçu du fichier</summary>
                      <p className="mt-2 text-xs text-gray-600 whitespace-pre-wrap">{fineAnalysisResult.summary}</p>
                    </details>
                  </div>
                </div>
              )}
            </div>

          {/* Onglets pour filtrer les amendes */}
          <div className="flex gap-2 mb-4 border-b border-gray-200 pb-2">
              <button
                onClick={() => setFineTab('all')}
                className={`px-4 py-2 rounded-t-lg transition-colors ${
                  fineTab === 'all'
                    ? 'bg-blue-500 text-white font-semibold'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Tous ({fines.length})
              </button>
              <button
                onClick={() => setFineTab('amende_simple')}
                className={`px-4 py-2 rounded-t-lg transition-colors ${
                  fineTab === 'amende_simple'
                    ? 'bg-orange-500 text-white font-semibold'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Amendes simples ({fines.filter(f => f.reason === 'amende_simple').length})
              </button>
              <button
                onClick={() => setFineTab('exces_vitesse')}
                className={`px-4 py-2 rounded-t-lg transition-colors ${
                  fineTab === 'exces_vitesse'
                    ? 'bg-red-500 text-white font-semibold'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Excès de vitesse ({fines.filter(f => f.reason === 'exces_vitesse').length})
              </button>
            </div>

          {loadingFines ? (
              <div className="flex justify-center py-6">
                <Spinner size="medium" />
              </div>
          ) : filteredFines.length === 0 ? (
              <div className="py-6 text-center">
                <p className="text-gray-500">
                  {fineTab === 'all'
                    ? 'Aucune amende enregistrée pour ce chauffeur.'
                    : fineTab === 'amende_simple'
                    ? 'Aucune amende simple enregistrée.'
                    : 'Aucun excès de vitesse enregistré.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
              {filteredFines.map((fine) => (
                <Card
                  key={fine._id}
                    className={`border-l-4 ${fine.isPaid ? 'border-success bg-green-50' : 'border-warning bg-orange-50'}`}
                  >
                    <div
                      onClick={() => setExpandedFine(expandedFine === fine._id ? null : fine._id)}
                      className="cursor-pointer p-4 flex justify-between items-center hover:bg-opacity-75 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Receipt className={`w-5 h-5 ${fine.isPaid ? 'text-success' : 'text-warning'}`} />
                        <div>
                          <h3 className="font-semibold text-gray-900">
                          {REASON_LABELS[fine.reason] || fine.reason} - {fine.amount}€
                          </h3>
                          <p className="text-sm text-gray-600">
                          Réf: {fine.referenceNumber} -{" "}
                            {fine.date ? formatDate(fine.date) : "Date non spécifiée"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                      <Chip
                        size="small"
                        label={fine.isPaid ? "Payée" : "Non payée"}
                        color={fine.isPaid ? "success" : "warning"}
                      />
                      {expandedFine === fine._id ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </div>
                    </div>

                    {expandedFine === fine._id && (
                      <div className="p-4 bg-white border-t border-gray-200">
                        <p className="text-sm mb-2">
                        <strong>Lieu:</strong> {fine.location}
                        </p>

                        {Array.isArray(fine.images) && fine.images.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium mb-2">Photos:</p>
                            <div className="flex flex-wrap gap-2">
                            {fine.images.map((img, idx) => (
                                <div
                                key={idx}
                                  className="w-20 h-20 rounded border border-gray-300 overflow-hidden cursor-pointer hover:opacity-75 transition-opacity"
                                onClick={() => handleOpenImage(img)}
                              >
                                <img
                                  src={img}
                                  alt={`Amende ${idx + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-2 mt-4">
                        <Button
                            variant="outlined"
                          size="small"
                            startIcon={<Edit className="w-4 h-4" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenFineForm(fine);
                          }}
                        >
                          Modifier
                        </Button>
                        <Button
                            variant="outlined"
                          color="error"
                            size="small"
                            startIcon={<Trash2 className="w-4 h-4" />}
                          onClick={(e) => {
                            e.stopPropagation();
                              openDeleteConfirmation("fine", fine._id);
                          }}
                        >
                          Supprimer
                        </Button>
                        </div>
                      </div>
                    )}
                </Card>
              ))}
              </div>
            )}
          </div>

          {/* Section Constats */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-error" />
              Constats ({reports.length})
              </h2>
            <Button
              variant="contained"
                color="error"
              size="small"
                startIcon={<Plus className="w-4 h-4" />}
              onClick={() => handleOpenReportForm()}
            >
              Ajouter un constat
            </Button>
            </div>

            {/* Analyse de constat (upload et extraction) */}
            <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h3 className="text-sm font-semibold text-purple-900 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Analyse automatique d'un sinistre/constat
              </h3>
              <p className="text-xs text-purple-700 mb-3">
                Uploadez un fichier (PDF, image, texte) de constat pour extraire automatiquement les informations.
              </p>
              <input
                type="file"
                accept=".pdf,.txt,.jpg,.jpeg,.png"
                onChange={handleReportFile}
                className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-purple-600 file:text-white hover:file:bg-purple-700"
              />
              {analyzingReport && (
                <div className="mt-3 flex items-center gap-2">
                  <Spinner size="small" />
                  <span className="text-sm text-gray-600">Analyse en cours...</span>
                </div>
              )}
              {reportAnalysisError && (
                <Alert severity="error" className="mt-3">
                  {reportAnalysisError}
                </Alert>
              )}
              {reportAnalysisResult && (
                <div className="mt-3 p-3 bg-white border border-green-300 rounded">
                  <h4 className="text-sm font-semibold text-green-800 mb-2">✅ Données extraites :</h4>
                  <div className="space-y-1 text-sm">
                    <p><strong>Référence :</strong> {reportAnalysisResult.reference}</p>
                    <p><strong>Type :</strong> {reportAnalysisResult.type}</p>
                    <p><strong>Description :</strong> {reportAnalysisResult.description}</p>
                    <p><strong>Lieu :</strong> {reportAnalysisResult.location}</p>
                    <details className="mt-2">
                      <summary className="cursor-pointer text-purple-600 hover:text-purple-800">Voir l'aperçu du fichier</summary>
                      <p className="mt-2 text-xs text-gray-600 whitespace-pre-wrap">{reportAnalysisResult.summary}</p>
                    </details>
                  </div>
                </div>
              )}
            </div>

            {/* Onglets pour filtrer les constats par responsabilité */}
            <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-200 pb-2">
              <button
                onClick={() => setReportTab('all')}
                className={`px-4 py-2 rounded-t-lg transition-colors ${
                  reportTab === 'all'
                    ? 'bg-blue-500 text-white font-semibold'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Tous ({reports.length})
              </button>
              <button
                onClick={() => setReportTab('non_responsable')}
                className={`px-4 py-2 rounded-t-lg transition-colors ${
                  reportTab === 'non_responsable'
                    ? 'bg-green-500 text-white font-semibold'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Non responsable ({reports.filter(r => r.responsabiliteChauffeur === 'non_responsable').length})
              </button>
              <button
                onClick={() => setReportTab('responsable_50')}
                className={`px-4 py-2 rounded-t-lg transition-colors ${
                  reportTab === 'responsable_50'
                    ? 'bg-orange-500 text-white font-semibold'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                50% responsable ({reports.filter(r => r.responsabiliteChauffeur === 'responsable_50').length})
              </button>
              <button
                onClick={() => setReportTab('responsable_100')}
                className={`px-4 py-2 rounded-t-lg transition-colors ${
                  reportTab === 'responsable_100'
                    ? 'bg-red-500 text-white font-semibold'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                100% responsable ({reports.filter(r => r.responsabiliteChauffeur === 'responsable_100').length})
              </button>
            </div>

          {loadingReports ? (
              <div className="flex justify-center py-6">
                <Spinner size="medium" />
              </div>
          ) : filteredReports.length === 0 ? (
              <div className="py-6 text-center">
                <p className="text-gray-500">
                  {reportTab === 'all'
                    ? 'Aucun constat enregistré pour ce chauffeur.'
                    : reportTab === 'non_responsable'
                    ? 'Aucun constat où le chauffeur est non responsable.'
                    : reportTab === 'responsable_50'
                    ? 'Aucun constat où le chauffeur est responsable à 50%.'
                    : 'Aucun constat où le chauffeur est responsable à 100%.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
              {filteredReports.map((report) => (
                <Card
                  key={report._id}
                    className={`border-l-4 ${report.isResolved ? 'border-success bg-green-50' : 'border-error bg-red-50'}`}
                  >
                    <div
                      onClick={() => setExpandedReport(expandedReport === report._id ? null : report._id)}
                      className="cursor-pointer p-4 flex justify-between items-center hover:bg-opacity-75 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <AlertCircle className={`w-5 h-5 ${report.isResolved ? 'text-success' : 'text-error'}`} />
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {report.type}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Réf: {report.referenceNumber} - {report.location}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                      <Chip
                        size="small"
                          label={report.isResolved ? "Résolu" : "En cours"}
                        color={report.isResolved ? "success" : "error"}
                      />
                      {expandedReport === report._id ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </div>
                    </div>

                    {expandedReport === report._id && (
                      <div className="p-4 bg-white border-t border-gray-200">
                        <p className="text-sm mb-2">
                        <strong>Description:</strong> {report.description}
                        </p>
                        <p className="text-sm mb-2">
                          <strong>Date:</strong>{" "}
                          {report.date ? formatDate(report.date) : "Date non spécifiée"}
                        </p>

                        {Array.isArray(report.images) && report.images.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium mb-2">Photos:</p>
                            <div className="flex flex-wrap gap-2">
                            {report.images.map((img, idx) => (
                                <div
                                key={idx}
                                  className="w-20 h-20 rounded border border-gray-300 overflow-hidden cursor-pointer hover:opacity-75 transition-opacity"
                                onClick={() => handleOpenImage(img)}
                              >
                                <img
                                  src={img}
                                  alt={`Constat ${idx + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-2 mt-4">
                              <Button
                                variant="outlined"
                                size="small"
                            startIcon={<Edit className="w-4 h-4" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenReportForm(report);
                          }}
                        >
                          Modifier
                        </Button>
                        <Button
                            variant="outlined"
                          color="error"
                            size="small"
                            startIcon={<Trash2 className="w-4 h-4" />}
                          onClick={(e) => {
                            e.stopPropagation();
                              openDeleteConfirmation("report", report._id);
                          }}
                        >
                          Supprimer
                        </Button>
                        </div>
                      </div>
                    )}
                </Card>
              ))}
              </div>
            )}
          </div>

          {/* Section Dégâts */}
          <DamagesSection
            driverId={driverKey || ""}
            onUpdate={() => {
              fetchDriverHistory();
            }}
          />

          {/* Historique des affectations */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Car className="w-6 h-6 text-primary" />
              Historique des affectations ({history?.history?.length || 0})
            </h2>

            {history?.history && Array.isArray(history.history) && history.history.length > 0 ? (
              <div className="space-y-3">
                {history.history.map((assignment) => (
                  <Card key={assignment._id} className="border-l-4 border-primary">
                    <CardContent>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
                            <Car className="w-5 h-5" />
                          {assignment.vehiclePlate}
                          </h3>
                          <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {assignment.date}
                            </span>
                            {assignment.time && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {assignment.time}
                              </span>
                            )}
                          </div>
                        </div>
                          <Chip
                          label={assignment.status}
                            color={
                            assignment.status === "terminée" ? "success" :
                            assignment.status === "en_cours" ? "warning" : "default"
                          }
                          size="small"
                        />
                      </div>

                      {assignment.damages && Array.isArray(assignment.damages) && assignment.damages.length > 0 && (
                        <div className="mt-4 p-3 bg-gray-50 rounded">
                          <p className="text-sm font-medium mb-2">Dégâts causés ({assignment.damages.length}):</p>
                          <div className="space-y-1">
                            {assignment.damages.map((damage: any, idx: number) => (
                              <div key={idx} className="flex items-center gap-2 text-sm">
                                <span className={`font-medium ${
                                  damage.type === 'Rayure' ? 'text-orange-600' :
                                  damage.type === 'Choc' ? 'text-red-600' :
                                  damage.type === 'Manquant' ? 'text-purple-600' :
                                  damage.type === 'Casse' ? 'text-red-800' : 'text-gray-600'
                                }`}>
                                  {damage.type}
                                </span>
                                <span className="text-gray-500">-</span>
                                <span className="text-gray-700">{damage.face || 'Face non spécifiée'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Bouton pour voir les détails du processus */}
                      {assignment._id && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <Button
                            to={`/vehicules/historique/${assignment.vehiclePlate}/processus/${assignment._id}`}
                            variant="contained"
                            size="small"
                            startIcon={<ExternalLink className="w-4 h-4" />}
                            className="w-full sm:w-auto"
                          >
                            Voir les détails du processus
                          </Button>
                        </div>
                      )}
                  </CardContent>
                </Card>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center">
                <p className="text-gray-500">Aucune affectation dans l'historique.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dialog Formulaire Amende */}
      <Dialog open={fineFormOpen} onClose={handleCloseFineForm} maxWidth="sm" fullWidth>
        <DialogTitle onClose={handleCloseFineForm}>
              {isEditing ? "Modifier l'amende" : "Ajouter une amende"}
            </DialogTitle>
            <DialogContent>
          <div className="space-y-4">
            <Input
                  fullWidth
              type="date"
                  label="Date"
                  name="date"
                  value={fineFormData.date}
                  onChange={handleFineFormChange}
                />

            <Input
                  fullWidth
              type="number"
                  label="Montant (€)"
                  name="amount"
                  value={fineFormData.amount}
                  onChange={handleFineFormChange}
                />

            <Select
                  fullWidth
              label="Raison"
                  name="reason"
                  value={fineFormData.reason}
                  onChange={handleFineFormChange}
                  options={[
                    { value: "", label: "Sélectionner une raison" },
                    { value: "amende_simple", label: "Amende simple" },
                    { value: "exces_vitesse", label: "Excès de vitesse" },
                  ]}
                />

            <Input
                  fullWidth
                  label="Lieu"
                  name="location"
                  value={fineFormData.location}
                  onChange={handleFineFormChange}
                />

            <Input
                  fullWidth
                  label="Numéro de référence"
                  name="referenceNumber"
                  value={fineFormData.referenceNumber}
                  onChange={handleFineFormChange}
                />

                    <Switch
              label="Amende payée"
              name="isPaid"
                      checked={fineFormData.isPaid}
                      onChange={handleFineFormChange}
              color="success"
            />

            {/* Upload d'images */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Photos</p>
                  <input
                    accept="image/*"
                    id="fine-image-upload"
                    type="file"
                    multiple
                    onChange={handleFineImageUpload}
                className="hidden"
                  />
                  <label htmlFor="fine-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                  startIcon={<ImageIcon className="w-4 h-4" />}
                      disabled={uploadingFineImage}
                    >
                  {uploadingFineImage ? "Téléchargement..." : "Ajouter des photos"}
                    </Button>
                  </label>

              {Array.isArray(previewFineImages) && previewFineImages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                      {previewFineImages.map((preview, idx) => (
                    <div key={idx} className="relative w-20 h-20 rounded border border-gray-300 overflow-hidden">
                          <img
                            src={preview}
                        alt={`Preview ${idx + 1}`}
                        className="w-full h-full object-cover"
                          />
                          <IconButton
                            size="small"
                        className="absolute top-0 right-0 bg-white bg-opacity-70"
                        onClick={() => removeFinePreviewImage(idx)}
                      >
                        <X className="w-3 h-3" />
                          </IconButton>
                    </div>
                      ))}
                </div>
                  )}
            </div>
          </div>
            </DialogContent>
            <DialogActions>
          <Button onClick={handleCloseFineForm} variant="text">Annuler</Button>
              <Button
            onClick={handleFineSubmit}
                variant="contained"
            startIcon={<Save className="w-4 h-4" />}
                disabled={uploadingFineImage}
              >
            {isEditing ? "Modifier" : "Ajouter"}
              </Button>
            </DialogActions>
          </Dialog>

      {/* Dialog Formulaire Constat */}
      <Dialog open={reportFormOpen} onClose={handleCloseReportForm} maxWidth="sm" fullWidth>
        <DialogTitle onClose={handleCloseReportForm}>
              {isEditing ? "Modifier le constat" : "Ajouter un constat"}
            </DialogTitle>
            <DialogContent>
          <div className="space-y-4">
            <Input
                  fullWidth
              type="date"
                  label="Date de l'accident"
                  name="dateAccident"
                  value={reportFormData.dateAccident}
                  onChange={handleReportFormChange}
                />

            <Input
                  fullWidth
              type="date"
                  label="Date de sa déclaration"
                  name="dateDeclaration"
                  value={reportFormData.dateDeclaration}
                  onChange={handleReportFormChange}
                />

            <Input
                  fullWidth
                  label="Type"
                  name="type"
                  value={reportFormData.type}
                  onChange={handleReportFormChange}
              placeholder="Ex: Accident, Panne, etc."
            />

            <Select
                  fullWidth
                  label="Type de choc initial"
                  name="typeChocInitial"
                  value={reportFormData.typeChocInitial}
                  onChange={handleReportFormChange}
                  options={[
                    { value: "", label: "Sélectionner un type de choc" },
                    ...TYPE_CHOC_OPTIONS,
                  ]}
                />

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Description
              </label>
              <textarea
                  name="description"
                  value={reportFormData.description}
                  onChange={handleReportFormChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <Input
                  fullWidth
                  label="Lieu"
                  name="location"
                  value={reportFormData.location}
                  onChange={handleReportFormChange}
                />

            <Input
                  fullWidth
                  label="Numéro de référence de l'assurance"
                  name="referenceNumber"
                  value={reportFormData.referenceNumber}
                  onChange={handleReportFormChange}
                />

            <Switch
              label="Dommage corporel"
              name="dommageCorporel"
              checked={reportFormData.dommageCorporel}
              onChange={handleReportFormChange}
              color="error"
            />

            <Switch
              label="Constat transmis"
              name="constatTransmis"
              checked={reportFormData.constatTransmis}
              onChange={handleReportFormChange}
              color="success"
            />

            <Select
                  fullWidth
                  label="Responsabilité du chauffeur"
                  name="responsabiliteChauffeur"
                  value={reportFormData.responsabiliteChauffeur}
                  onChange={handleReportFormChange}
                  options={[
                    { value: "", label: "Sélectionner la responsabilité" },
                    ...RESPONSABILITE_OPTIONS,
                  ]}
                />

                    <Switch
              label="Constat résolu"
              name="isResolved"
                      checked={reportFormData.isResolved}
                      onChange={handleReportFormChange}
              color="success"
            />

            {/* Upload d'images */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Photos</p>
                  <input
                    accept="image/*"
                    id="report-image-upload"
                    type="file"
                    multiple
                    onChange={handleReportImageUpload}
                className="hidden"
                  />
                  <label htmlFor="report-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                  startIcon={<ImageIcon className="w-4 h-4" />}
                      disabled={uploadingReportImage}
                    >
                  {uploadingReportImage ? "Téléchargement..." : "Ajouter des photos"}
                    </Button>
                  </label>

              {Array.isArray(previewReportImages) && previewReportImages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                      {previewReportImages.map((preview, idx) => (
                    <div key={idx} className="relative w-20 h-20 rounded border border-gray-300 overflow-hidden">
                          <img
                            src={preview}
                        alt={`Preview ${idx + 1}`}
                        className="w-full h-full object-cover"
                          />
                          <IconButton
                            size="small"
                        className="absolute top-0 right-0 bg-white bg-opacity-70"
                        onClick={() => removeReportPreviewImage(idx)}
                      >
                        <X className="w-3 h-3" />
                          </IconButton>
                    </div>
                      ))}
                </div>
                  )}
            </div>
          </div>
            </DialogContent>
            <DialogActions>
          <Button onClick={handleCloseReportForm} variant="text">Annuler</Button>
              <Button
            onClick={handleReportSubmit}
                variant="contained"
            color="error"
            startIcon={<Save className="w-4 h-4" />}
                disabled={uploadingReportImage}
              >
            {isEditing ? "Modifier" : "Ajouter"}
              </Button>
            </DialogActions>
          </Dialog>

      {/* Dialog Confirmation Suppression */}
      <Dialog open={deleteConfirmationOpen} onClose={() => setDeleteConfirmationOpen(false)} maxWidth="xs">
        <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogContent>
          <p className="text-gray-700">
            Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.
          </p>
            </DialogContent>
            <DialogActions>
          <Button onClick={() => setDeleteConfirmationOpen(false)} variant="text">
            Annuler
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
                Supprimer
              </Button>
            </DialogActions>
          </Dialog>

      {/* Dialog Image */}
          <Dialog
            open={imageDialogOpen}
            onClose={() => setImageDialogOpen(false)}
            maxWidth="md"
      >
        <DialogTitle onClose={() => setImageDialogOpen(false)}>Image</DialogTitle>
        <DialogContent>
              {currentImage && (
                <img
                  src={currentImage}
              alt="Détail"
              className="w-full h-auto"
                />
              )}
            </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)} variant="contained">
            Fermer
          </Button>
        </DialogActions>
          </Dialog>

      {/* Snackbar */}
      {snackbarOpen && (
        <div className="fixed bottom-4 right-4 z-50">
          <Alert 
            severity="success"
            onClose={() => setSnackbarOpen(false)}
          >
            {snackbarMessage}
          </Alert>
        </div>
      )}
    </div>
  );
};

export default DriverHistoryPage;
